import { Flex, Modal } from "@mantine/core";
import { useIsDesktop } from "hooks/useMediaQueries";
import { ReactNode } from "react";
import ActionButtons from "./ActionButtons";
import FormModalContent from "./FormModalContent";
import FormModalFooter from "./FormModalFooter";
import FormModalHeader from "./FormModalHeader";

interface BaseProps {
  title: ReactNode;
  opened: boolean;
  children: ReactNode;
  isPending: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

interface OptionsProps {
  fullScreen?: boolean;
  fillHeight?: boolean;
  withMenu?: boolean;
  withOverlay?: boolean;
  isEdit?: boolean;
  trapFocus?: boolean;
  xOffset?: number;
  yOffset?: number;
  yOffsetBottom?: number;
  gap?: number;
  size?: string;
  showSaveButton?: boolean;
  saveDisabled?: boolean;
  saveButtonLabel?: string;
  customButtonGroup?: ReactNode;
  zIndex?: number;
  noContentScroll?: boolean;
}

interface FunctionProps {
  onClickMenu?: () => void;
}

type FormModalProps = BaseProps & OptionsProps & FunctionProps;

const FormModal: React.FC<FormModalProps> = ({
  title,
  opened,
  children,
  isPending,
  fullScreen = true,
  fillHeight = false,
  withMenu = false,
  withOverlay = true,
  isEdit = false,
  trapFocus = false,
  xOffset = 20,
  yOffset = 16,
  yOffsetBottom = 20,
  gap = 20,
  size = "md",
  showSaveButton = true,
  saveDisabled = false,
  saveButtonLabel,
  customButtonGroup,
  zIndex,
  noContentScroll = false,
  onClose,
  onClickMenu,
  onSubmit,
}) => {
  const isDesktop = useIsDesktop();
  const effectiveFullScreen = fullScreen ? isDesktop !== true : false;

  return (
    <Modal
      xOffset={isDesktop ? "5vw" : 0}
      yOffset={isDesktop ? "5dvh" : 0}
      opened={opened}
      onClose={onClose}
      fullScreen={effectiveFullScreen}
      withOverlay={withOverlay}
      zIndex={zIndex}
      size={!effectiveFullScreen ? size : undefined}
      centered={!effectiveFullScreen && !fillHeight}
      withCloseButton={false}
      trapFocus={trapFocus}
      styles={
        !effectiveFullScreen
          ? {
              content: {
                ...(fillHeight ? { height: "100%" } : { maxHeight: "80vh" }),
                display: "flex",
                flexDirection: "column",
              },
              body: { flex: 1, minHeight: 0 },
            }
          : undefined
      }
      classNames={{
        body: "!p-0 h-full",
        header: "!p-0",
        content: "relative",
        inner: !effectiveFullScreen && !fillHeight ? "!p-0" : undefined,
      }}
    >
      <Flex
        pos={"relative"}
        direction={"column"}
        h={"100%"}
        mah={"100%"}
        w={"100%"}
        style={{ minHeight: 0 }}
      >
        <div
          className="grid min-w-0 grid-rows-[auto_1fr_auto]"
          style={{ height: "100%", minHeight: 0 }}
        >
          <FormModalHeader
            title={title}
            isDesktop={!effectiveFullScreen}
            isEdit={isEdit}
            goBack={onClose}
            onClickMenu={onClickMenu}
            withMenu={withMenu}
          />
          <FormModalContent
            gap={gap}
            xOffset={xOffset}
            yOffset={yOffset}
            yOffsetBottom={yOffsetBottom}
            isDesktop={!effectiveFullScreen}
            noScroll={noContentScroll}
          >
            {children}
          </FormModalContent>
          <FormModalFooter>
            {customButtonGroup ? (
              customButtonGroup
            ) : (
              <ActionButtons
                showSaveButton={showSaveButton}
                saveDisabled={saveDisabled}
                saveButtonLabel={saveButtonLabel}
                onClickConfirm={onSubmit}
                isPending={isPending}
                onClickCancel={onClose}
              />
            )}
          </FormModalFooter>
        </div>
      </Flex>
    </Modal>
  );
};

export default FormModal;
